/** Vertical extent of one rendered text fragment, in viewport coordinates. */
export type LineBox = {
  top: number
  bottom: number
}

/**
 * Groups text fragments into visual lines and returns each line's bottom, top to bottom.
 *
 * @remarks
 * A fragment joins the current line when it overlaps it by more than half of the shorter of the
 * two, so mixed font sizes or inline elements on one line (bold text, links) count once, while
 * adjacent lines whose glyph boxes overlap slightly (tight heading line-height) stay separate.
 */
export function getLineBottoms(boxes: readonly LineBox[]): number[] {
  const lines: LineBox[] = []

  for (const box of [...boxes].sort((a, b) => a.top - b.top)) {
    const line = lines.at(-1)
    const overlap = line ? Math.min(line.bottom, box.bottom) - Math.max(line.top, box.top) : 0
    const shorter = line ? Math.min(line.bottom - line.top, box.bottom - box.top) : 0

    if (line && overlap > shorter / 2) {
      line.top = Math.min(line.top, box.top)
      line.bottom = Math.max(line.bottom, box.bottom)
    } else {
      lines.push({ ...box })
    }
  }

  return lines.map((line) => line.bottom)
}

const getTextBoxes = (element: HTMLElement): LineBox[] => {
  const boxes: LineBox[] = []
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
  const range = document.createRange()

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.textContent?.trim()) continue

    range.selectNodeContents(node)
    for (const { top, bottom, height } of range.getClientRects()) {
      if (height > 0) boxes.push({ top, bottom })
    }
  }

  return boxes
}

/**
 * Returns the height from the top of `element` that shows exactly its first `lines` lines of
 * text, or `null` when all of its text fits within `lines`.
 *
 * @remarks
 * Measures the rendered line boxes, so a heading or list item counts as the lines it occupies
 * and the cut never falls inside a line. Line boxes keep their layout position while `element`
 * is clipped by `max-height`, so it can be measured in the collapsed state.
 */
export function getTextLineClampHeight(element: HTMLElement, lines: number): number | null {
  const bottoms = getLineBottoms(getTextBoxes(element))
  if (bottoms.length <= lines) return null

  return Math.ceil(bottoms[lines - 1] - element.getBoundingClientRect().top)
}
